import React, { useState, useRef, useEffect } from "react";
import {
  faCheck,
  faTriangleExclamation
} from "@fortawesome/pro-solid-svg-icons";
import { faFloppyDisk, faCircleXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { BakeryItem } from "../types";
import { getArea, getTimeStamp, currentDateTime } from "./functions";

interface TempCheckState {
  TimeStamp: string;
  Temperature: string;
  Initials: string;
}

const TempCheck: React.FC<{
  item: BakeryItem;
  storeName: string;
  isFOH: boolean;
  updateSavedTempChecks: (PLU: number) => void;
  removeSavedTempCheck: (PLU: number) => void;
  onShiftInitials?: string;
  updateOnShiftInitials: (initials: string) => void;
  bakeTableState: BakeryItem[];
  setBakeTableState: (items: BakeryItem[]) => void;
  clearTempChecks: (number | null)[];
}> = ({
  item,
  storeName,
  isFOH,
  updateSavedTempChecks,
  removeSavedTempCheck,
  onShiftInitials,
  updateOnShiftInitials,
  bakeTableState,
  setBakeTableState,
  clearTempChecks
}) => {
  const [initials, setInitials] = useState("");
  const [tempChecksState, setTempChecksState] =
    useState<TempCheckState | null>();
  const [temperature, setTemperature] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);

  const initialsRef = useRef<HTMLInputElement | null>(null);

  const handleInitialsChange = (event: { target: { value: string } }) => {
    setInitials(event.target.value);
  };

  const tempRef = useRef<HTMLInputElement | null>(null);

  const handleTempChange = (event: { target: { value: string } }) => {
    setTemperature(event.target.value);
  };

  const handleTempSubmit = async () => {
    setIsProcessing(true);

    window.dataLayer.push({
      event: "Temp check",
      bakery: storeName,
      bakery_area: getArea(isFOH),
      product: item.product_name,
      quantity: item.product_units.toString(),
      value: temperature,
      initials: initials,
      time: getTimeStamp()
    });

    if (initialsRef.current) {
      initialsRef.current?.blur();
    }

    if (tempRef.current) {
      tempRef.current?.blur();
    }

    try {
      localStorage.setItem(
        item.PLU.toString(),
        JSON.stringify({
          TimeStamp: currentDateTime(),
          Temperature: temperature,
          Initials: initials
        })
      );

      saveTempCheck({
        TimeStamp: currentDateTime(),
        Temperature: temperature,
        Initials: initials
      });
      setIsProcessing(false);
    } catch {
      setIsProcessing(false);
      setIsError(true);
      setTimeout(() => {
        setIsError(false);
      }, 3000);
    }
  };

  const handleClear = () => {
    clearTempCheck();
  };

  const clearTempCheck = () => {
    localStorage.removeItem(item.PLU.toString());
    setTempChecksState(null);
    onShiftInitials && setInitials(onShiftInitials);
    setIsSuccess(false);
    setTemperature("");
    removeSavedTempCheck(item.PLU);
    const newBakeryItems = [...bakeTableState];
    const index = newBakeryItems.findIndex(
      (bakeryItem) => bakeryItem.PLU === item.PLU
    );
    newBakeryItems[index].temp_check_timestamp = undefined;
    newBakeryItems[index].temp_check_temperature = undefined;
    newBakeryItems[index].temp_check_initials = undefined;
    if (index !== -1) {
      setBakeTableState(newBakeryItems);
    }
  };

  const saveTempCheck = (check: TempCheckState) => {
    setTempChecksState({
      TimeStamp: check.TimeStamp,
      Temperature: check.Temperature,
      Initials: check.Initials
    });
    updateOnShiftInitials(check.Initials);

    updateSavedTempChecks(item.PLU);

    const newBakeryItems = [...bakeTableState];
    const index = newBakeryItems.findIndex(
      (bakeryItem) => bakeryItem.PLU === item.PLU
    );
    newBakeryItems[index].temp_check_timestamp = check.TimeStamp;
    newBakeryItems[index].temp_check_temperature = parseFloat(
      check.Temperature
    );
    newBakeryItems[index].temp_check_initials = check.Initials;
    if (index !== -1) {
      setBakeTableState(newBakeryItems);
    }
    setIsSuccess(true);
  };

  useEffect(() => {
    if (clearTempChecks.includes(item.PLU)) {
      clearTempCheck();
    } else {
      const savedTempCheck = localStorage.getItem(item.PLU.toString());
      if (savedTempCheck) {
        saveTempCheck(JSON.parse(savedTempCheck));
      } else {
        onShiftInitials && setInitials(onShiftInitials);
      }
    }
  }, [clearTempChecks]);

  return (
    <div className="bakery-table__temp-check temp-check jr-temp-check active">
      <form
        onSubmit={(event) => {
          handleTempSubmit();
          event.preventDefault();
        }}
      >
        <input
          ref={initialsRef}
          type="text"
          className="units units--input hlpr-input"
          onChange={(event) => handleInitialsChange(event)}
          placeholder={tempChecksState?.Initials ?? "Init."}
          value={initials ?? ""}
          disabled={isSuccess}
        />
        <input
          ref={tempRef}
          type="number"
          min="75"
          max="130"
          step="0.1"
          className="units units--input hlpr-input"
          onChange={(event) => handleTempChange(event)}
          placeholder={tempChecksState?.Temperature ?? "°C"}
          value={temperature ?? ""}
          disabled={isSuccess}
        />
        <button
          className={`temp-check__button${
            item.temp_check === "Always"
              ? " temp-check__button--red"
              : " temp-check__button--amber"
          }`}
          type="submit"
          disabled={
            !initials || !temperature || isProcessing || isSuccess || isError
          }
        >
          {isProcessing && (
            <div className="loading-overlay__spinner hidden">
              <svg
                aria-hidden="true"
                focusable="false"
                className="spinner"
                viewBox="0 0 66 66"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  className="path"
                  fill="none"
                  strokeWidth="6"
                  cx="33"
                  cy="33"
                  r="30"
                ></circle>
              </svg>
            </div>
          )}
          {isSuccess && <FontAwesomeIcon icon={faCheck} />}
          {isError && <FontAwesomeIcon icon={faTriangleExclamation} />}
          {!(isProcessing || isSuccess || isError) && (
            <FontAwesomeIcon icon={faFloppyDisk} />
          )}
        </button>
      </form>
      {tempChecksState ? (
        <div className="temp-check__time-stamp hlpr-flexbox">
          <small>{tempChecksState.TimeStamp.slice(11, 16)}</small>
          <FontAwesomeIcon
            icon={faCircleXmark}
            className="hlpr-clear-input clear-temp-check jr-clear-temp-check"
            onClick={() => handleClear()}
          />
        </div>
      ) : null}
    </div>
  );
};

export default TempCheck;
